import { type CurrencyAmount, formatAmounts, formatPLN, type Money, NBSP } from '@fos/shared/money';
import {
  formatClock as sharedFormatClock,
  formatDuration as sharedFormatDuration,
  toLocalDate,
  toLocalTime,
} from '@fos/shared/time';

/**
 * Formatowanie po polsku (pl-PL, Europe/Warsaw). Kwoty i czas z @fos/shared (jedna implementacja dla UI, PDF i CSV).
 * Separator tysięcy i odstęp przed „zł” to niełamliwa spacja (NBSP).
 */

type DateLike = Date | string | number;

const MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
] as const;
const WEEKDAYS_LONG = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'] as const;
const WEEKDAYS_SHORT = ['nd', 'pn', 'wt', 'śr', 'czw', 'pt', 'sb'] as const;
const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Części daty w Europe/Warsaw. Ciąg "YYYY-MM-DD" traktujemy jako datę biznesową (bez strefy). */
function localParts(value: DateLike): { y: number; m: number; d: number; weekday: number } {
  const local = typeof value === 'string' && LOCAL_DATE_RE.test(value) ? value : toLocalDate(toDate(value));
  const [y, m, d] = local.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return { y, m, d, weekday };
}

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(value);
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase('pl-PL') + text.slice(1);
}

// ── Kwoty ───────────────────────────────────────────────────────────────────

/** Kwota z groszami: "250,00 zł", "4 275,50 zł", "120,00 EUR" (wpisy, pozycje zestawień). */
export function formatMoney(amount: Money | number, currency = 'PLN'): string {
  return formatPLN(String(amount), { currency });
}

/** Kwota bez groszy, zaokrąglona do złotówki: "4 275 zł" (kafelki, sumy). */
export function formatMoneyShort(amount: Money | number, currency = 'PLN'): string {
  return formatPLN(String(amount), { decimals: false, currency });
}

/** Bez groszy, gdy kwota jest całkowita ("4 275 zł"), z groszami w pozostałych przypadkach ("4 275,50 zł"). */
export function formatMoneyAuto(amount: Money | number, currency = 'PLN'): string {
  const text = String(amount).trim().replace(',', '.');
  const whole = /^-?\d+(\.0+)?$/.test(text);
  return formatPLN(text, { decimals: !whole, currency });
}

/** Stawka godzinowa: "150 zł/h", "120 EUR/h". */
export function formatRate(rate: Money | number, currency = 'PLN'): string {
  return `${formatMoneyAuto(rate, currency)}/h`;
}

/**
 * Suma w kilku walutach (waluty się nie sumuje): kwota główna w `currency` + `otherAmounts`, np. „1 200 zł + 850 EUR”.
 * `short` = zaokrąglone do całości (kafelki), inaczej bez groszy tylko dla kwot całkowitych.
 */
export function formatMoneyTotal(
  total: { amount: Money; currency?: string; otherAmounts?: readonly CurrencyAmount[] },
  options: { short?: boolean } = {},
): string {
  return formatAmounts([{ amount: total.amount, currency: total.currency ?? 'PLN' }, ...(total.otherAmounts ?? [])], {
    decimals: options.short ? false : 'auto',
  });
}

// ── Czas pracy ──────────────────────────────────────────────────────────────

/** 24 000 s → "6 h 40 min". */
export function formatDuration(seconds: number): string {
  return sharedFormatDuration(seconds).replaceAll(' ', NBSP);
}

/** 28 800 s → "8:00" (do wyświetlania w <Mono>). */
export function formatClock(seconds: number): string {
  return sharedFormatClock(seconds);
}

// ── Daty ────────────────────────────────────────────────────────────────────

/** "Niedziela, 4 października" */
export function formatDayLong(value: DateLike): string {
  const { d, m, weekday } = localParts(value);
  return `${capitalize(WEEKDAYS_LONG[weekday]!)}, ${d} ${MONTHS_GENITIVE[m - 1]}`;
}

/** "4 października" (z rokiem, gdy inny niż bieżący: "4 października 2027"). */
export function formatDateLong(value: DateLike, now: Date = new Date()): string {
  const { y, d, m } = localParts(value);
  const currentYear = localParts(now).y;
  return `${d} ${MONTHS_GENITIVE[m - 1]}${y !== currentYear ? ` ${y}` : ''}`;
}

/** "nd 4.10" */
export function formatDayShort(value: DateLike): string {
  const { d, m, weekday } = localParts(value);
  return `${WEEKDAYS_SHORT[weekday]} ${d}.${String(m).padStart(2, '0')}`;
}

/** Nazwa dnia tygodnia: "czwartek". */
export function formatWeekday(value: DateLike): string {
  return WEEKDAYS_LONG[localParts(value).weekday]!;
}

/** "8.10" (krótka data w bieżącym roku) albo "8.10.2027". */
export function formatDateShort(value: DateLike, now: Date = new Date()): string {
  const { y, d, m } = localParts(value);
  const base = `${d}.${String(m).padStart(2, '0')}`;
  return y === localParts(now).y ? base : `${base}.${y}`;
}

/** "15.03.2027" */
export function formatDate(value: DateLike): string {
  const { y, d, m } = localParts(value);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

/** "10:00" (Europe/Warsaw) */
export function formatTime(value: DateLike): string {
  const [h, min] = toLocalTime(toDate(value)).split(':');
  return `${Number(h)}:${min}`;
}

/** "8.10 · 10:00" */
export function formatDateTimeShort(value: DateLike, now: Date = new Date()): string {
  return `${formatDateShort(value, now)} · ${formatTime(value)}`;
}

/** Czas względny w przeszłości: "przed chwilą", "12 min temu", "dziś, 9:42", "wczoraj, 18:05", "3.10 · 10:00". */
export function formatRelativePast(value: DateLike, now: Date = new Date()): string {
  const date = toDate(value);
  const diffSec = Math.round((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 60) return 'przed chwilą';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}${NBSP}min temu`;
  const today = toLocalDate(now);
  const day = toLocalDate(date);
  if (day === today) return `dziś, ${formatTime(date)}`;
  const yesterday = toLocalDate(new Date(now.getTime() - 86_400_000));
  if (day === yesterday) return `wczoraj, ${formatTime(date)}`;
  return formatDateTimeShort(date, now);
}

/** Pozostały czas blokady (429): 900 → "15 min", 45 → "45 s", 7200 → "2 h", 5400 → "1 h 30 min". */
export function formatWait(seconds: number): string {
  const s = Math.max(1, Math.ceil(seconds));
  if (s < 60) return `${s}${NBSP}s`;
  const minutes = Math.ceil(s / 60);
  if (minutes < 60) return `${minutes}${NBSP}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}${NBSP}h` : `${h}${NBSP}h ${m}${NBSP}min`;
}

/**
 * Wiek w kafelku (np. „najstarsza czeka …”): do godziny jak formatWait, potem pełne godziny, od doby dni.
 * 11 820 s → „3 h”, 172 800 s → „2 dni” (Figma 01: „najstarsza czeka 5 h”, kafelek bez wielokropka).
 */
export function formatAge(seconds: number): string {
  if (seconds < 3600) return formatWait(seconds);
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}${NBSP}h`;
  const days = Math.floor(seconds / 86_400);
  return `${days}${NBSP}${days === 1 ? 'dzień' : 'dni'}`;
}

// ── Teksty ──────────────────────────────────────────────────────────────────

/** Powitanie wg pory dnia (Europe/Warsaw): 4:00–17:59 „Dzień dobry”, potem „Dobry wieczór”. */
export function greeting(now: Date = new Date()): string {
  const hour = Number(toLocalTime(now).slice(0, 2));
  return hour >= 4 && hour < 18 ? 'Dzień dobry' : 'Dobry wieczór';
}

/**
 * Tytuł w środku zdania (podpisy kafelków, Figma: „czwartek · REA, przegląd FAQ”): mała pierwsza litera, chyba że
 * pierwsze słowo to skrót („FAQ”, „B2B”) — wtedy bez zmian.
 */
export function lowerInitial(text: string): string {
  const second = text.charAt(1);
  if (!second || second.toLocaleLowerCase('pl-PL') !== second || second.toLocaleUpperCase('pl-PL') === second) {
    return text;
  }
  return text.charAt(0).toLocaleLowerCase('pl-PL') + text.slice(1);
}

/** Pierwsze słowo imienia i nazwiska: "Sebastian Pietraszewski" → "Sebastian". */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** Odmiana liczebnika: plural(3, ['akcja', 'akcje', 'akcji']) → "akcje". */
export function plural(n: number, forms: readonly [one: string, few: string, many: string]): string {
  const abs = Math.abs(n);
  if (abs === 1) return forms[0];
  const lastTwo = abs % 100;
  const last = abs % 10;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return forms[1];
  return forms[2];
}
